import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import tipoMonedaService, { TipoMoneda } from '../services/tipoMonedaService';

interface TipoMonedaSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (tipoMoneda: TipoMoneda) => void;
}

const TipoMonedaSearch: React.FC<TipoMonedaSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [tiposMoneda, setTiposMoneda] = useState<TipoMoneda[]>([]);
    const [filteredTiposMoneda, setFilteredTiposMoneda] = useState<TipoMoneda[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadTiposMoneda = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await tipoMonedaService.getTiposMoneda();
            setTiposMoneda(data);
            setFilteredTiposMoneda(data);
        } catch (err: any) {
            setError(`Error al cargar tipos de moneda: ${err.message || 'Error desconocido'}`);
            setTiposMoneda([]);
            setFilteredTiposMoneda([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadTiposMoneda();
        } else {
            setSearchTerm('');
            setTiposMoneda([]);
            setFilteredTiposMoneda([]);
            setError(null);
        }
    }, [open, loadTiposMoneda]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredTiposMoneda(tiposMoneda);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = tiposMoneda.filter(tm =>
            tm.nombre?.toLowerCase().includes(term) ||
            tm.simbolo?.toLowerCase().includes(term) ||
            tm.codigoMoneda?.toString().includes(term) ||
            tm.tipoMoneda?.toString().includes(term)
        );
        setFilteredTiposMoneda(filtered);
    }, [searchTerm, tiposMoneda]);

    const handleSelect = (tipoMoneda: TipoMoneda) => {
        onSelect(tipoMoneda);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setTiposMoneda([]);
        setFilteredTiposMoneda([]);
        setError(null);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Tipo de Moneda
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Nombre, símbolo o código de moneda"
                        inputProps={{ 'aria-label': 'Buscar tipo de moneda' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {filteredTiposMoneda.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {filteredTiposMoneda.map((tipoMoneda) => (
                                        <Card 
                                            key={tipoMoneda.codigoMoneda || tipoMoneda.tipoMoneda} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(tipoMoneda)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Código: {tipoMoneda.codigoMoneda || tipoMoneda.tipoMoneda}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(tipoMoneda);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Nombre
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {tipoMoneda.nombre || '-'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Símbolo
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {tipoMoneda.simbolo || '-'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Símbolo</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredTiposMoneda.map((tipoMoneda) => (
                                            <TableRow 
                                                key={tipoMoneda.codigoMoneda || tipoMoneda.tipoMoneda} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(tipoMoneda)}
                                            >
                                                <TableCell>{tipoMoneda.codigoMoneda || tipoMoneda.tipoMoneda}</TableCell>
                                                <TableCell>{tipoMoneda.nombre || '-'}</TableCell>
                                                <TableCell>{tipoMoneda.simbolo || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(tipoMoneda);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && filteredTiposMoneda.length === 0 && searchTerm && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron tipos de moneda
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TipoMonedaSearch;
